import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import { LitigationEventHeading } from "@/Components/DocumentModal/DocumentSideBar/LitigationDocumentMenu";

import { formatDateForPanelDisplay } from "../../Utils/helper";
import avatarImage from "../../assets/images/avatar.svg";
import UserChatBubble from "../Forms/FirmUserBtn";
import { useDocumentModal } from "../common/CustomModal/CustomModalContext";
import mixColorWithWhite from "../TreatmentPage/utils/helperFn";
import "./DocSummaryView.css";

const renderPanelName = (doc) => {
  const panelName = doc.panel.panel_name;
  const insurancePartyMatch =
    doc.page.id === 11 ? panelName?.match(/^(Client|Defendant|Witness|OtherParty)\b\s*/i) : null;
  const insuranceParty = insurancePartyMatch?.[1].toLowerCase();
  const insurancePartyIcon = {
    otherparty: "ic-parties",
    defendant: "ic-defendants",
    witness: "ic-witnesses",
  }[insuranceParty];

  return (
    <>
      {insuranceParty === "client" && (
        <img
          src={doc.for_client?.profile_pic_19p || doc.for_client?.profile_pic_29p || avatarImage}
          alt="Client"
          className="ic ic-15 flex-shrink-0"
          style={{
            width: "15px",
            height: "15px",
            borderRadius: "50%",
            objectFit: "cover",
            background: "var(--primary-30)",
          }}
        />
      )}
      {insurancePartyIcon && (
        <i
          className={`ic ic-15 ${insurancePartyIcon}`}
          role="img"
          aria-label={insurancePartyMatch[1]}
        />
      )}
      <span className="doc-summary-label">
        {insurancePartyMatch ? panelName.slice(insurancePartyMatch[0].length) : panelName}
      </span>
    </>
  );
};

// Function to render data rows
const renderDataRow = (
  setRowRef,
  tableRowStyle,
  doc,
  index,
  userOnlineStatus,
  handleDocPreview
) => (
  <tr
    ref={(el) => setRowRef(el, index)}
    key={`defendant-${index}`}
    className="defendant-height-205 doc-summary-row"
    onClick={() => handleDocPreview(doc)}
    style={tableRowStyle}
  >
    <td
      className="text-center"
      style={{
        minWidth: "25px",
        width: "25px",
        maxWidth: "25px",
        color: "var(--primary-25)",
        fontWeight: "600",
      }}
    >
      {index + 1}
    </td>

    <td className="doc-summary-name p-l-5">
      <div className="doc-summary-field">
        <i className="ic ic-docs ic-15" aria-hidden="true" />
        <span className="doc-summary-label">{doc?.file_name}</span>
      </div>
    </td>
    <td className="doc-summary-spacer" aria-hidden="true" />
    <td className="doc-summary-meta">
      <div
        style={{
          fontWeight: "600",
        }}
        className="d-flex align-items-center text-center justify-content-center"
      >
        {doc?.page_count ?? ""}
      </div>
    </td>
    <td className="doc-summary-spacer" aria-hidden="true" />
    <td className="doc-summary-meta">
      <div
        style={{
          fontWeight: "600",
          textTransform: "lowercase",
        }}
        className="d-flex align-items-center text-center justify-content-center"
      >
        {doc?.file_size ?? ""}
      </div>
    </td>
    <td className="doc-summary-spacer" aria-hidden="true" />

    <td className="doc-summary-location p-l-5">
      <div className="doc-summary-fields">
        <div className="doc-summary-field">
          {!doc?.page && (
            <img
              src="https://simplefirm-bucket.s3.amazonaws.com/static/images/documents-icon-color_b6TvcB7.svg"
              alt=""
              width={15}
              height={15}
              className="ic ic-docs ic-15"
            />
          )}
          {doc?.page?.page_icon && (
            <img
              src={doc.page.page_icon}
              alt=""
              width={15}
              height={15}
              className="ic ic-docs ic-15"
            />
          )}
          <span className="doc-summary-label">
            {doc?.page ? `${doc.page.name} Page` : "UNSORTED"}
          </span>
        </div>
        {doc?.page && doc?.panel && (
          <div
            className={`doc-summary-field${
              doc.panel.panel_type === "Treatment" ? " doc-summary-treatment" : ""
            }`}
            style={
              doc.panel.panel_type === "Treatment"
                ? {
                    background: doc.panel.specialty?.color
                      ? mixColorWithWhite(doc.panel.specialty.color, 10)
                      : "var(--primary-15)",
                  }
                : undefined
            }
          >
            {doc.panel.panel_type === "Treatment" && (
              <span
                className="doc-summary-specialty"
                style={{ background: doc.panel.specialty?.color || "#000" }}
              >
                {doc.panel.specialty?.name?.[0] || "?"}
              </span>
            )}
            {doc.page.id === 19 && doc.panel.litigation_data ? (
              <LitigationEventHeading panel={doc.panel.litigation_data} showNumber={false} />
            ) : (
              renderPanelName(doc)
            )}
          </div>
        )}
        {doc?.page &&
          (doc?.panel ||
            (doc?.document_slot !== null && doc?.document_slot?.slot_number !== 0)) && (
            <div className="doc-summary-field">
              {Boolean(doc?.document_slot?.slot_number) && (
                <span className="doc-summary-slot-number">{doc.document_slot.slot_number}:</span>
              )}
              <span className="doc-summary-label">
                {doc?.document_slot?.slot_name ?? doc?.file_name}
              </span>
            </div>
          )}
      </div>
    </td>

    <td className="doc-summary-spacer" aria-hidden="true" />
    <td colSpan={2} className="doc-summary-added">
      <div className="doc-summary-added-fields">
        <span className="doc-summary-added-date">{formatDateForPanelDisplay(doc?.created)}</span>
        <div className="doc-summary-added-user">
          <UserChatBubble
            userId={doc?.attached_by?.id}
            first_name={doc?.attached_by?.first_name ?? ""}
            last_name={doc?.attached_by?.last_name ?? ""}
            profilePic={doc?.attached_by?.profile_pic_29p ?? ""}
            is_active={doc?.attached_by?.is_active}
            width={19}
            height={25}
            isActive={userOnlineStatus?.[doc?.attached_by?.id]?.isOnline ?? false}
          />
        </div>
      </div>
    </td>
    <td />
  </tr>
);

const renderEmptyRows = (emptyRowStyle, tableRowStyle, fakeRows) => {
  const emptyRowsCount = Math.max(0, fakeRows);

  return Array.from({ length: emptyRowsCount }, (_, index) => (
    <tr key={`empty-${index}`} style={tableRowStyle}>
      <td colSpan={12} className="empty-row">
        <div style={emptyRowStyle}>{/* Empty row */}</div>
      </td>
    </tr>
  ));
};

const renderPartialRow = (partialHeight) => {
  if (partialHeight <= 0) return null;
  return (
    <tr
      key="partial-row"
      style={{ height: `${partialHeight}px`, lineHeight: `${partialHeight}px` }}
    >
      <td colSpan={12} className="empty-row" style={{ height: `${partialHeight}px` }}>
        <div style={{ height: `${partialHeight}px`, width: "100%" }} />
      </td>
    </tr>
  );
};

function DocSummaryView({ doc = [], containerHeight, refetchData }) {
  const [lineHeight, setLineHeight] = useState(35);
  const userOnlineStatus = useSelector((state) => state.chat2.userOnlineStatus);
  const { showDocumentModal } = useDocumentModal();

  const tableRowStyle = {
    height: `${lineHeight}px`,
    lineHeight: `${lineHeight}px`,
  };

  const emptyRowStyle = {
    height: `${lineHeight}px`,
    width: "100%",
  };

  const tableContainerRef = useRef(null);
  const rowRefs = useRef({});
  const setRowRef = useCallback((el, index) => {
    if (el) {
      rowRefs.current[index] = el;
    } else {
      delete rowRefs.current[index];
    }
  }, []);

  const [fakeRows, setFakeRows] = useState(0);
  const [partialHeight, setPartialHeight] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const [tableMaxHeight, setTableMaxHeight] = useState(undefined);

  // Reserve the actual shared badge width, including updates from the
  // global longest-name equalization, before allocating space to text.
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const badges = container.querySelectorAll(".doc-summary-added-user > div");
    const reserveAddedWidth = () => {
      const badgeWidth = Math.max(
        88,
        ...Array.from(badges, (badge) => badge.getBoundingClientRect().width)
      );
      container.style.setProperty("--doc-summary-added-width", `${Math.ceil(badgeWidth) + 12}px`);
    };
    reserveAddedWidth();
    const observer = new ResizeObserver(reserveAddedWidth);
    badges.forEach((badge) => observer.observe(badge));
    return () => observer.disconnect();
  }, [doc?.results]);

  // Measure the contents rather than the previous row height so rows can
  // shrink again when Location fields fit on one line after a resize.
  useEffect(() => {
    const measureRows = () => {
      const heights = Object.values(rowRefs.current).flatMap((row) =>
        Array.from(row.cells, (cell) => {
          const styles = getComputedStyle(cell);
          const contentHeight = Math.max(
            21,
            ...Array.from(cell.children, (child) => child.getBoundingClientRect().height)
          );
          return contentHeight + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
        })
      );
      setLineHeight(Math.ceil(Math.max(35, ...heights)));
    };

    measureRows();
    const observer = new ResizeObserver(measureRows);
    if (tableContainerRef.current) observer.observe(tableContainerRef.current);
    Object.values(rowRefs.current).forEach((row) => {
      Array.from(row.cells).forEach((cell) => {
        Array.from(cell.children).forEach((child) => observer.observe(child));
      });
    });
    return () => observer.disconnect();
  }, [doc?.results]);

  useEffect(() => {
    const container = containerHeight?.current;
    const tableContainer = tableContainerRef.current;
    if (!container || !tableContainer) return;
    const header = tableContainer.querySelector("thead");

    const updateFillRows = () => {
      const tableOffset =
        tableContainer.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      const maxHeight = Math.max(0, container.clientHeight - tableOffset - 5);
      const availableHeight = Math.max(0, maxHeight - (header?.offsetHeight || 25));
      const dataLength = doc?.results?.length || 0;
      const totalRows = Math.floor(availableHeight / lineHeight);
      const remainder = availableHeight - totalRows * lineHeight;
      const dataOverflows = dataLength > totalRows;

      setFakeRows(dataOverflows ? 0 : totalRows - dataLength);
      setPartialHeight(dataOverflows ? 0 : remainder);
      setCanScroll(dataOverflows);
      setTableMaxHeight(maxHeight);
    };

    updateFillRows();
    const observer = new ResizeObserver(updateFillRows);
    observer.observe(container);
    if (header) observer.observe(header);
    return () => observer.disconnect();
  }, [containerHeight, doc?.results?.length, lineHeight]);

  const handleDocPreview = (doc) => {
    axios
      .get(`${origin}/api/attorney-staff/${doc.attached_by?.id}/profile-image/`)
      .then((response) => {
        doc.attached_by.profile_pic_29p = response.data.profile_image;
      })
      .catch((error) => {
        console.error(`Error fetching profile image: ${error}`);
      });

    showDocumentModal("document", doc.upload, doc, refetchData);
  };

  return (
    <div className="custom-tab lit-custom-tab w-100 m-b-5">
      {/* Table */}
      <div
        ref={tableContainerRef}
        className="table-responsive doc-summary-responsive"
        style={{
          maxHeight: tableMaxHeight,
          overflowY: canScroll ? "auto" : "hidden",
          scrollbarWidth: "none",
        }}
      >
        <table className="table table-borderless table-striped table-treatment has-height-25 doc-summary-table">
          <colgroup>
            <col className="doc-summary-index-column" />
            <col className="doc-summary-name-column" />
            <col className="doc-summary-spacer-column" />
            <col className="doc-summary-pages-column" />
            <col className="doc-summary-spacer-column" />
            <col className="doc-summary-size-column" />
            <col className="doc-summary-spacer-column" />
            <col className="doc-summary-location-column" />
            <col className="doc-summary-spacer-column" />
            <col className="doc-summary-date-column" />
            <col className="doc-summary-user-column" />
            <col className="doc-summary-edge-column" />
          </colgroup>
          <thead>
            <tr id="tb-header">
              <th
                className="text-center"
                style={{
                  minWidth: "25px",
                  width: "25px",
                  maxWidth: "25px",
                }}
              />
              <th className="td-autosize text-center color-grey-2 p-l-5">Document Name</th>
              <th className="doc-summary-spacer" aria-hidden="true" />
              <th className="td-autosize text-center color-grey-2">Pages</th>
              <th className="doc-summary-spacer" aria-hidden="true" />
              <th className="td-autosize text-center color-grey-2">Size</th>
              <th className="doc-summary-spacer" aria-hidden="true" />
              <th className="text-center color-grey-2 p-l-5">Location</th>
              <th className="doc-summary-spacer" aria-hidden="true" />
              <th className="td-autosize text-center color-grey-2" colSpan={2}>
                Added
              </th>
              <th
                style={{
                  minWidth: "5px",
                  maxWidth: "5px",
                  width: "5px",
                }}
              />
            </tr>
          </thead>
          <tbody>
            {doc?.results?.map((doc1, index) =>
              renderDataRow(
                setRowRef,
                tableRowStyle,
                doc1,
                index,
                userOnlineStatus,
                handleDocPreview
              )
            )}
            {!canScroll && renderEmptyRows(emptyRowStyle, tableRowStyle, fakeRows)}
            {!canScroll && renderPartialRow(partialHeight)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(DocSummaryView);
